import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import { getSupplierNoticeForView, deleteSupplierNotice, sendNoticeEmail } from '../../../models/noticesModel';
import { useAuth } from '../../../context/AuthContext';

function SupplierNoticeView() {
  const { noticeId } = useParams();
  const navigate = useNavigate();
  const { user, can } = useAuth();
  const [notice, setNotice] = useState(null);
  const [groups, setGroups] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Send mail form state
  const [sendTo, setSendTo] = useState('0');
  const [selectedSuppliers, setSelectedSuppliers] = useState([]);
  const [selectedGroup, setSelectedGroup] = useState('0');
  const [selectedProject, setSelectedProject] = useState('0');
  
  // State for supplier search and filtering
  const [supplierSearch, setSupplierSearch] = useState('');
  const [supplierGroupFilter, setSupplierGroupFilter] = useState('');
  const [supplierGroups, setSupplierGroups] = useState([]);

  useEffect(() => {
    if (user && noticeId) {
      fetchNoticeData();
    }
  }, [user, noticeId]);

  const fetchNoticeData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await getSupplierNoticeForView(noticeId);
      
      if (response.status === 1) {
        if (response.data.row) {
          setNotice(response.data.row);
          setGroups(response.data.groups || []);
          setSuppliers(response.data.suppliers || []);
          setProjects(response.data.projects || []);
        } else {
          throw new Error('Invalid response structure');
        }
      } else {
        throw new Error(response.message || 'Failed to load notice');
      }
    } catch (err) {
      console.error('Error fetching notice:', err);
      setError(err.message);
      toast.error(err.message || 'Failed to load notice');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = () => {
    navigate(`/dashboard/notices/supplier/edit/${noticeId}`);
  };

  const handleDelete = async () => {
    const result = await Swal.fire({
      title: 'Delete Notice',
      text: 'Are you sure you want to delete this notice? This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#dc2626',
      cancelButtonColor: '#64748b',
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'Cancel'
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteSupplierNotice(noticeId, notice?.notice_title || '');
        if (response.status === 1) {
          toast.success('Notice deleted successfully');
          navigate('/dashboard/notices/supplier');
        } else {
          throw new Error(response.message || 'Failed to delete notice');
        }
      } catch (err) {
        console.error('Error deleting notice:', err);
        toast.error(err.message || 'Failed to delete notice');
      }
    }
  };

  const handleSendMail = async (e) => {
    e.preventDefault();
    
    if (sendTo === '0') {
      toast.warning('Please select who to send the notice to');
      return;
    }
    
    // Validate based on send option
    if (sendTo === '1' && selectedSuppliers.length === 0) {
      toast.warning('Please select at least one supplier');
      return;
    }
    
    if (sendTo === '2' && (!selectedGroup || selectedGroup === '0')) {
      toast.warning('Please select a supplier group');
      return;
    }
    
    if (sendTo === '3' && (!selectedProject || selectedProject === '0')) {
      toast.warning('Please select a project');
      return;
    }
    
    try {
      // Prepare send data
      const sendData = {
        specification_send_to: sendTo
      };
      
      if (sendTo === '1') {
        // Selected suppliers - send as comma-separated string
        sendData.BP_Code = selectedSuppliers.join(',');
      } else if (sendTo === '2') {
        // Group
        sendData.send_group = selectedGroup;
      } else if (sendTo === '3') {
        // Project
        sendData.send_project = selectedProject;
      }
      
      const response = await sendNoticeEmail(noticeId, sendData);
      
      if (response.status === 1) {
        const count = response.data?.count || 0;
        const sent = response.data?.sent || [];
        
        await Swal.fire({
          title: 'Success!',
          html: `
            <p>Notice sent successfully to <strong>${count}</strong> recipient(s)</p>
            ${sent.length > 0 ? `<div class="mt-3"><small>Sent to: ${sent.join(', ')}</small></div>` : ''}
          `,
          icon: 'success',
          confirmButtonColor: '#28a745'
        });
        
        // Reset form
        setSendTo('0');
        setSelectedSuppliers([]);
        setSelectedGroup('0');
        setSelectedProject('0');
      } else {
        throw new Error(response.message || 'Failed to send notice');
      }
    } catch (err) {
      console.error('Error sending notice:', err);
      toast.error(err.message || 'Failed to send notice');
    }
  };

  const handleBack = () => {
    navigate('/dashboard/notices/supplier');
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return '';
    const date = new Date(timestamp * 1000);
    return date.toLocaleString('en-GB', {
      day: '2-digit',
      month: 'long',
      year: '2-digit',
      weekday: 'short',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="p-6 md:p-10 flex justify-center items-center h-[calc(100vh-150px)]">
        <div 
          className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4" 
          style={{ borderColor: 'var(--color-secondary-default)' }}
        ></div>
      </div>
    );
  }

  if (error || !notice) {
    return (
      <div className="max-w-6xl mx-auto p-6">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-red-800 mb-2">Error Loading Notice</h2>
          <p className="text-red-600">{error || 'Notice not found'}</p>
          <button
            onClick={handleBack}
            className="mt-4 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
          >
            Back to Notices
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-6">
      {/* Header with Back Navigation */}
      <div className="mb-6">
        <button
          onClick={handleBack}
          className="flex items-center text-[#0087D4] hover:text-blue-600 transition-colors mb-4"
        >
          <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
          </svg>
          Back to Supplier Notices
        </button>
      </div>

      <form onSubmit={handleSendMail}>
        <div className="grid grid-cols-1 lg:grid-cols-6 gap-6">
          {/* Main Content */}
          <div className="lg:col-span-5">
            <div className="bg-white rounded-lg shadow border">
              <div className="p-6 border-b">
                <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                  {/* Title and Date */}
                  <div className="flex-1">
                    <h2 className="text-2xl font-bold text-gray-900 mb-2">{notice.notice_title}</h2>
                    <p className="text-sm text-gray-600">{formatDate(notice.notice_time)}</p>
                  </div>
                  
                  {/* Send To Dropdown */}
                  <div className="lg:w-48">
                    <label htmlFor="send_to" className="block text-sm font-medium text-gray-700 mb-1">
                      Send to:
                    </label>
                    <select
                      id="send_to"
                      name="specification_send_to"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm"
                      value={sendTo}
                      onChange={(e) => setSendTo(e.target.value)}
                      required
                    >
                      <option value="0">None</option>
                      <option value="1">Selected</option>
                      <option value="2">Group</option>
                      <option value="3">Project</option>
                      <option value="4">All</option>
                    </select>
                  </div>
                </div>

                {/* Conditional Send Options */}
                {sendTo === '1' && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      Select Suppliers
                    </label>
                    {/* Dual Pane Supplier Selection */}
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                      {/* Available Suppliers */}
                      <div className="border border-gray-300 rounded-lg">
                        <div className="bg-gray-50 px-4 py-3 border-b border-gray-300">
                          <h4 className="text-sm font-medium text-gray-900 mb-2">Available Suppliers</h4>
                          <div className="space-y-2">
                            <input
                              type="text"
                              placeholder="Search suppliers..."
                              value={supplierSearch}
                              onChange={(e) => setSupplierSearch(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0087D4] focus:border-[#0087D4]"
                            />
                            <select
                              value={supplierGroupFilter}
                              onChange={(e) => setSupplierGroupFilter(e.target.value)}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#0087D4] focus:border-[#0087D4]"
                            >
                              <option value="">All groups</option>
                              {supplierGroups.map(group => (
                                <option key={group} value={group}>{group}</option>
                              ))}
                            </select>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Click on a supplier to add it to the selected list</p>
                        </div>
                        <div className="max-h-64 overflow-y-auto p-2">
                          {suppliers
                            .filter(supplier => {
                              const notSelected = !selectedSuppliers.includes(supplier.BP_Code);
                              const matchesSearch = !supplierSearch || 
                                (supplier.BP_Name && supplier.BP_Name.toLowerCase().includes(supplierSearch.toLowerCase())) ||
                                String(supplier.BP_Code || '').includes(supplierSearch);
                              const matchesGroup = !supplierGroupFilter || 
                                (supplier.Group_Code && supplier.Group_Code.split(',').map(g => g.trim()).includes(supplierGroupFilter));
                              return notSelected && matchesSearch && matchesGroup;
                            })
                            .map((supplier) => (
                              <div
                                key={supplier.BP_Code}
                                onClick={() => setSelectedSuppliers(prev => [...prev, supplier.BP_Code])}
                                className="flex items-center justify-between p-2 rounded-md hover:bg-green-50 cursor-pointer transition-colors duration-150 border-b border-gray-100 last:border-b-0"
                              >
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-gray-900">
                                    {supplier.BP_Name}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="ml-2 text-green-600 hover:text-green-800 flex-shrink-0"
                                >
                                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                  </svg>
                                </button>
                              </div>
                            ))}
                          {suppliers.filter(supplier => !selectedSuppliers.includes(supplier.BP_Code)).length === 0 && (
                            <div className="p-8 text-center text-gray-500">
                              <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                              </svg>
                              <p className="text-sm">No suppliers found</p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Selected Suppliers */}
                      <div className="border border-gray-300 rounded-lg">
                        <div className="bg-green-50 px-4 py-3 border-b border-gray-300">
                          <div className="flex items-center justify-between">
                            <h4 className="text-sm font-medium text-gray-900">
                              Selected Suppliers ({selectedSuppliers.length})
                            </h4>
                            <div className="flex items-center gap-2">
                              {selectedSuppliers.length > 0 && (
                                <button
                                  type="button"
                                  onClick={() => setSelectedSuppliers([])}
                                  className="text-xs text-red-600 hover:text-red-800 font-medium"
                                >
                                  Clear All
                                </button>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="max-h-64 overflow-y-auto p-2">
                          {selectedSuppliers.length === 0 ? (
                            <div className="p-8 text-center text-gray-500">
                              <svg className="w-12 h-12 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                              </svg>
                              <p className="text-sm">No suppliers selected</p>
                              <p className="text-xs text-gray-400 mt-1">Click suppliers from the left to add them</p>
                            </div>
                          ) : (
                            selectedSuppliers.map((code) => {
                              const supplier = suppliers.find(s => s.BP_Code === code);
                              return (
                                <div key={code} className="flex items-center justify-between p-2 rounded-md hover:bg-red-50 group transition-colors duration-150 border-b border-gray-100 last:border-b-0">
                                  <div className="flex-1">
                                    <div className="text-sm font-medium text-gray-900">
                                      {supplier ? supplier.BP_Name : code}
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {supplier ? supplier.BP_Code : ""}
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => setSelectedSuppliers(prev => prev.filter(c => c !== code))}
                                    className="ml-2 text-red-600 hover:text-red-800 flex-shrink-0 opacity-0 group-hover:opacity-100 transition-opacity"
                                  >
                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                                    </svg>
                                  </button>
                                </div>
                              );
                            })
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {sendTo === '2' && (
                  <div className="mt-4 space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        Select Supplier Group
                      </label>
                      <select
                        name="send_group"
                        className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm"
                        value={selectedGroup}
                        onChange={(e) => setSelectedGroup(e.target.value)}
                      >
                        <option value="">Select a supplier group</option>
                        {groups.map(group => (
                          <option key={group.Supplier_Group_Code} value={group.Supplier_Group_Code}>
                            {group.Supplier_Group_Name} ({group.suppliers})
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    {/* Preview suppliers in selected group */}
                    {selectedGroup && selectedGroup !== '0' && (
                      <div className="border border-gray-300 rounded-lg">
                        <div className="bg-blue-50 px-4 py-3 border-b border-gray-300">
                          <h4 className="text-sm font-medium text-gray-900">
                            Suppliers in selected group ({suppliers.filter(supplier => {
                              if (!supplier.Group_Code) return false;
                              const supplierGroups = supplier.Group_Code.split(',').map(g => g.trim());
                              return supplierGroups.includes(selectedGroup);
                            }).length})
                          </h4>
                          <p className="text-xs text-gray-500 mt-1">This notice will be sent to all suppliers in this group</p>
                        </div>
                        <div className="max-h-48 overflow-y-auto p-2">
                          {suppliers
                            .filter(supplier => {
                              if (!supplier.Group_Code) return false;
                              const supplierGroups = supplier.Group_Code.split(',').map(g => g.trim());
                              return supplierGroups.includes(selectedGroup);
                            })
                            .map((supplier) => (
                              <div key={supplier.BP_Code} className="flex items-center p-2 rounded-md border-b border-gray-100 last:border-b-0">
                                <div className="flex-1">
                                  <div className="text-sm font-medium text-gray-900">
                                    {supplier.BP_Name}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {supplier.BP_Code} • Groups: {supplier.Group_Code}
                                  </div>
                                </div>
                              </div>
                            ))}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {sendTo === '3' && (
                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select Project
                    </label>
                    <select
                      name="send_project"
                      className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-[#0087D4] focus:border-[#0087D4] sm:text-sm"
                      value={selectedProject}
                      onChange={(e) => setSelectedProject(e.target.value)}
                    >
                      <option value="0">None</option>
                      {projects.map(project => (
                        <option key={project.specification_id} value={project.specification_id}>
                          {project.specification_title}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              {/* Notice Content */}
              <div className="p-6">
                <div 
                  className="prose max-w-none text-gray-800"
                  dangerouslySetInnerHTML={{ __html: notice.notice_text }}
                />
              </div>
            </div>
          </div>

          {/* Actions Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow border p-6 sticky top-6">
              <div className="space-y-3">
                {can('Update Notice') && (
                  <button
                    type="button"
                    onClick={handleEdit}
                    className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-yellow-500 hover:bg-yellow-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-yellow-500"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                    </svg>
                    Edit
                  </button>
                )}

                {can('Delete Notice') && notice.notice_user_id == user?.user_id && (
                  <button
                    type="button"
                    onClick={handleDelete}
                    className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                  >
                    <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                    </svg>
                    Delete
                  </button>
                )}

                <button
                  type="submit"
                  className="w-full inline-flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#28a745] hover:bg-green-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                  </svg>
                  Send
                </button>
              </div>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
}

export default SupplierNoticeView;
